/**
 * Entrada na viewport — uma classe, um observer, zero dependência.
 *
 * POR QUE NÃO GSAP/ScrollTrigger, que já está no package.json: o efeito é
 * "apareceu → mostra", sem timeline, sem scrub e sem pin. ScrollTrigger
 * recalcula posições a cada resize e a cada refresh de layout; um
 * IntersectionObserver é resolvido pelo compositor do navegador e não roda
 * JavaScript por frame de rolagem. Numa página de doze dobras aberta no
 * celular do visitante, essa diferença é a diferença entre rolar liso e
 * engasgar.
 *
 * `once: true` por padrão — reanimar ao voltar é o tipo de efeito que encanta
 * na primeira passagem e irrita na terceira, e o visitante desta página sobe e
 * desce várias vezes entre o formulário e os palestrantes.
 */
import { useEffect, useRef } from 'react'

const VISIVEL = 'entra-visivel'

export function useEntrada({ margem = '0px 0px -12% 0px', limiar = 0.12 } = {}) {
  const ref = useRef(null)

  useEffect(() => {
    const raiz = ref.current
    if (!raiz) return

    const alvos = raiz.matches?.('.entra')
      ? [raiz, ...raiz.querySelectorAll('.entra')]
      : [...raiz.querySelectorAll('.entra')]
    if (alvos.length === 0) return

    // Navegador sem IntersectionObserver, ou pessoa que pediu menos movimento:
    // mostra tudo de uma vez. O CSS já cobre o segundo caso, mas mostrar aqui
    // também evita depender da ordem em que as folhas carregam.
    const semMovimento =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches

    if (typeof IntersectionObserver !== 'function' || semMovimento) {
      alvos.forEach((el) => el.classList.add(VISIVEL))
      return
    }

    const observer = new IntersectionObserver(
      (entradas) => {
        for (const entrada of entradas) {
          if (!entrada.isIntersecting) continue
          entrada.target.classList.add(VISIVEL)
          observer.unobserve(entrada.target)
        }
      },
      { rootMargin: margem, threshold: limiar },
    )

    // Elemento que JÁ está na primeira tela quando o script roda: mostrar
    // imediatamente, sem esperar o observer. O observer dispara em um frame
    // futuro, e nesse frame o hero apareceria piscando.
    for (const el of alvos) {
      const r = el.getBoundingClientRect()
      if (r.top < window.innerHeight && r.bottom > 0) el.classList.add(VISIVEL)
      else observer.observe(el)
    }

    return () => observer.disconnect()
  }, [margem, limiar])

  return ref
}

/**
 * Atraso em cascata para itens irmãos. Devolve o objeto de estilo, e não uma
 * classe, porque o índice é dado em tempo de render.
 *
 * TETO DE 6 PASSOS: numa grade de doze cartões, um atraso linear faria o
 * último esperar 0,72s depois do primeiro — a pessoa já rolou. O teto mantém a
 * cascata legível no começo e agrupa a cauda.
 */
export function atraso(indice, passo = 60) {
  return { transitionDelay: `${Math.min(indice, 6) * passo}ms` }
}
